/**
 * `BUCKETED_RUNS_DELTA_QUERY` is the canonical bucketed-runs query for the
 * non-internal-id code path. It exposes pinned runs at the top level via the
 * `pinnedRuns: runs(ids:)` alias, which is project-agnostic and supports
 * cross-project pins.
 *
 * `BUCKETED_RUNS_DELTA_QUERY_INTERNAL_ID` continues to use the legacy
 * `project.pinnedRuns: runs(filters:)` shape because the internal-id code path
 * has not been migrated yet. `DELTA_QUERY_LEGACY_FRAGMENT` and the legacy
 * result-shape handling in `applyPinnedSubQuery.ts` exist for that fragment.
 */
import gql from 'graphql-tag';

import {RUN_INFO_FRAGMENT} from '../../../../state/runs-low/graphql';
import {LINE_PLOT_RUN_FRAGMENT} from './linePlotRun.fragment';

export const RUNS_INFO = gql`
  query RunsInfo(
    $projectName: String!
    $entityName: String!
    $filters: JSONString!
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(filters: $filters) {
        edges {
          node {
            id
            ...RunInfoFragment
          }
        }
      }
    }
  }
  ${RUN_INFO_FRAGMENT}
`;

/**
 * Shared selection on BucketDiffs used by both the main `project.runs.deltas`
 * and the top-level `pinnedRuns: runs(ids:).deltas` selection. Keeping this
 * separate means the inner `delta { op run { ... } }` payload is defined once.
 */
export const BUCKETED_HISTORY_DELTAS_FRAGMENT = gql`
  fragment BucketedHistoryDeltasFragment on BucketDiffs {
    order
    incomplete
    delta {
      op
      run {
        ...LinePlotRunFragment
        bucketedHistory(
          specs: $bucketedHistorySpecs
          packVersion: 1
          excludeOutliers: $excludeOutliers
          primaryLineType: $primaryLineType
        )
        config(keys: $configKeys) @include(if: $enableConfig)
        defaultColorIndex
        displayName
        id
        name
        projectId
        summaryMetrics(keys: $summaryKeys) @include(if: $enableSummary)
        updatedAt
      }
    }
  }
  ${LINE_PLOT_RUN_FRAGMENT}
`;

export const RUN_DELTAS_FRAGMENT = gql`
  fragment RunDeltasFragment on RunConnection {
    deltas(
      currentRuns: $currentRuns
      lastUpdated: $lastUpdated
      timeout: $timeout
    ) {
      ...BucketedHistoryDeltasFragment
    }
  }
  ${BUCKETED_HISTORY_DELTAS_FRAGMENT}
`;

export const DELTA_QUERY_FRAGMENT = gql`
  fragment DeltaQueryFragmentWithoutRunInfo on Project {
    entityName
    name
    runs(
      first: $limit
      order: $order
      filters: $filters
      groupKeys: $groupKeys
      groupLevel: $groupLevel
    ) {
      ...RunDeltasFragment
    }
  }
  ${RUN_DELTAS_FRAGMENT}
`;

const DELTA_QUERY_LEGACY_FRAGMENT = gql`
  fragment DeltaQueryLegacyFragmentWithoutRunInfo on Project {
    entityName
    name
    pinnedRuns: runs(filters: $pinnedRunFilters, first: $pinnedRunsLimit)
      @include(if: $includePinnedRuns) {
      ...RunDeltasFragment
    }
    runs(
      first: $limit
      order: $order
      filters: $filters
      groupKeys: $groupKeys
      groupLevel: $groupLevel
    ) {
      ...RunDeltasFragment
    }
  }
  ${RUN_DELTAS_FRAGMENT}
`;

export const BUCKETED_RUNS_DELTA_QUERY = gql`
  # Inlined literal — codegen cannot resolve template-string interpolations.
  # See bucketedData/queries/architecture.md §11 (GraphQL Operation Name Convention).
  query BucketedRunsDeltaQuery_DD_DQ_U_BRDQ(
    $bucketedHistorySpecs: [JSONString!]!
    $configKeys: [String!]
    $currentRuns: [String!]!
    $enableConfig: Boolean = false
    $enableSummary: Boolean = false
    $entityName: String!
    $excludeOutliers: Boolean
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $includePinnedRuns: Boolean = false
    $lastUpdated: DateTime!
    $limit: Int!
    $order: String!
    $pinnedRunIds: [ID!]!
    $projectName: String!
    $summaryKeys: [String!]
    $timeout: Int
    $primaryLineType: String
  ) {
    pinnedRuns: runs(ids: $pinnedRunIds) @include(if: $includePinnedRuns) {
      deltas(
        currentRuns: $currentRuns
        lastUpdated: $lastUpdated
        timeout: $timeout
      ) {
        ...BucketedHistoryDeltasFragment
      }
    }
    project(name: $projectName, entityName: $entityName) {
      id
      ...DeltaQueryFragmentWithoutRunInfo
    }
  }
  ${BUCKETED_HISTORY_DELTAS_FRAGMENT}
  ${DELTA_QUERY_FRAGMENT}
`;

export const BUCKETED_RUNS_DELTA_QUERY_INTERNAL_ID = gql`
  # Inlined literal — codegen cannot resolve template-string interpolations.
  # See bucketedData/queries/architecture.md §11 (GraphQL Operation Name Convention).
  query BucketedRunsInternalIdDeltaQuery_DD_DQ_U_BRDQ(
    $bucketedHistorySpecs: [JSONString!]!
    $configKeys: [String!]
    $currentRuns: [String!]!
    $enableConfig: Boolean = false
    $enableSummary: Boolean = false
    $excludeOutliers: Boolean
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $includePinnedRuns: Boolean = false
    $internalId: ID!
    $lastUpdated: DateTime!
    $limit: Int!
    $order: String!
    $pinnedRunFilters: JSONString
    $pinnedRunsLimit: Int!
    $summaryKeys: [String!]
    $timeout: Int
    $primaryLineType: String
  ) {
    project(internalId: $internalId) {
      id
      ...DeltaQueryLegacyFragmentWithoutRunInfo
    }
  }
  ${DELTA_QUERY_LEGACY_FRAGMENT}
`;
